/**
 * Sheet colours: the user markup palette and the paper. UI chrome colours are the Broadsheet kit's tokens
 * (styles/tokens.css), never TS literals (spec--pmt-ui-redesign S9).
 */

/** The drawing's paper: a sheet stays white paper in both themes (spec--pmt-ui-redesign 5(a)). What the app paints AS
 *  paper -- a rendered tile's background, an image's flattened alpha, a markup label's halo, a callout's default fill,
 *  a handle's face -- uses this; chrome reads the kit tokens instead. Allowlisted as sheet content (src/test/sourceScan.ts). */
export const SHEET_PAPER = '#ffffff';

/** The ink chrome draws ON the paper: the kit's light --bk-text, which the sheet scope keeps in both themes. For paint
 *  that cannot read a CSS custom property (a canvas 2D bitmap, a value shared with one); SVG on the sheet uses
 *  `currentColor` (the sheet scope's text colour) instead. styles/kitTheme.test.ts holds it equal to the kit's. */
export const SHEET_INK = '#141414';

/** The ink of a markup that carries no colour of its own (a markup without a style; a bim-pdf annotation without one).
 *  Sheet content, like IMPORTED_ANNOT_DEFAULT_COLOR (D18). */
export const UNSTYLED_MARKUP_COLOR = '#ff0000';

/** The default ink of the markups the BIM tools make (auto count, element move) when the drawing style has none.
 *  Sheet content (D16). */
export const BIM_MARKUP_COLOR = '#d32f2f';

/** The colour a new BIM overlay rule starts with (the user then picks one): sheet content, like the class colours (D16). */
export const BIM_RULE_DEFAULT_COLOR = '#9e9e9e';

// Markup Colors (8 distinct, high contrast on cream canvas)
// Adjusted from PRD to meet WCAG AA 4.5:1 contrast requirement
export const MARKUP_COLORS = [
    '#a12a61', // Magenta (default) = the Broadsheet spot colour (--bk-spot, light): the markup ink, not UI colour (D2); 7.0:1 on white
    '#c43d00', // Orange - darkened from #fd521e
    '#007835', // Lime green - darkened significantly from #00d962
    '#736800', // Yellow - darkened very significantly from #fff310
    '#d00024', // Red - darkened from #f2002c
    '#00796a', // Cyan - darkened significantly from #00dfba
    '#7d00d4', // Purple - darkened from #ab1aff
    '#232323', // Black - already passes
] as const;

/**
 * Convert hex color to RGB values
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
    // Remove # if present
    const cleanHex = hex.replace(/^#/, '');

    // Handle 3-char hex (#fff -> #ffffff)
    const fullHex = cleanHex.length === 3
        ? cleanHex.split('').map(char => char + char).join('')
        : cleanHex;

    const r = parseInt(fullHex.substring(0, 2), 16);
    const g = parseInt(fullHex.substring(2, 4), 16);
    const b = parseInt(fullHex.substring(4, 6), 16);

    return { r, g, b };
}

/**
 * Calculate relative luminance according to WCAG formula
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(hex: string): number {
    const { r, g, b } = hexToRgb(hex);

    // Convert to 0-1 range
    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    // Apply sRGB gamma correction
    const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    // Calculate relative luminance
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Calculate WCAG contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 *
 * @param color1 - First color in hex format (#rrggbb or #rgb)
 * @param color2 - Second color in hex format (#rrggbb or #rgb)
 * @returns Contrast ratio (1 to 21)
 */
export function getContrastRatio(color1: string, color2: string): number {
    const lum1 = getRelativeLuminance(color1);
    const lum2 = getRelativeLuminance(color2);

    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);

    return (lighter + 0.05) / (darker + 0.05);
}
